/**
 * Drop-in "Export CSV" button used by the bulk-export pages.
 *
 *   <ExportCsvButton resource="employees" />
 *
 * Handles the full download flow: shows a "Downloading…" toast,
 * triggers the browser save dialog, and surfaces a friendly error
 * toast on 4xx/5xx (the most common failure being a 403 for non-HR
 * roles — the button still renders, but the API enforces the gate).
 *
 * Pages that gate visibility on role should hide this button for
 * non-HR users; the API enforces it independently.
 */
import { useState } from "react";
import { Download, Loader2 } from "lucide-react";

import { exportResourceCsv, type CsvExportResource } from "@/api/csvExport";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/hooks/useToast";

interface ExportCsvButtonProps {
  resource: CsvExportResource;
  /** Optional ``YYYY-MM-DD`` lower bound (inclusive). */
  from?: string;
  /** Optional ``YYYY-MM-DD`` upper bound (inclusive). */
  to?: string;
  /** Visual variant — pages with a primary CTA pass "ghost". */
  variant?: "primary" | "secondary" | "ghost";
  size?: "sm" | "md";
  className?: string;
  label?: string;
}

export function ExportCsvButton({
  resource,
  from,
  to,
  variant = "ghost",
  size = "sm",
  className,
  label = "Export CSV",
}: ExportCsvButtonProps) {
  const { addToast } = useToast();
  const [busy, setBusy] = useState(false);

  async function onClick() {
    setBusy(true);
    addToast("info", "Downloading export…");
    try {
      await exportResourceCsv(resource, { from, to });
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Export failed.";
      addToast("error", detail);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button
      variant={variant}
      size={size}
      onClick={onClick}
      disabled={busy}
      className={className}
    >
      {busy ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Download className="h-4 w-4" />
      )}
      {label}
    </Button>
  );
}

export default ExportCsvButton;
